import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ChevronLeft, X, Sparkles, Trophy, Users, Brain, History, Rocket } from "lucide-react";

/**
 * Guided welcome modal for first-time demo visitors.
 * Instead of Joyride overlay tour (which had rendering issues), we use a
 * lightweight modal carousel with 6 steps that highlight the app's value.
 */
const STEPS = [
  {
    icon: Sparkles,
    title: "🎮 Benvenuto nella Demo",
    body: "Questa è una lega completa e reale (copiata dalla Lega dei Magnati con nomi cambiati). Puoi navigare tutto: mercato, contratti, chiamare l'AI. Nessun addebito, nessuna registrazione.",
    tone: "amber",
  },
  {
    icon: Users,
    title: "⚽ Sei l'admin di una squadra",
    body: "Sei stato assegnato come manager di una squadra. Vedrai un budget iniziale, 27 giocatori nella tua rosa, storico personale, contratti in scadenza — esattamente come nella lega vera.",
    tone: "sky",
  },
  {
    icon: Trophy,
    title: "🏆 Rose complete e Finanze avanzate",
    body: "Nella sezione Rosa trovi i tuoi giocatori con ruoli Mantra, valori aggiornati, ROI e stipendi. Nelle Finanze bilanci, patrimonio, andamento budget. Nello Stadio i livelli e il merchandising.",
    tone: "emerald",
  },
  {
    icon: Brain,
    title: "🧠 AI Coach con Claude Sonnet 5",
    body: "Vai su AI Coach per un parere dettagliato sulla tua rosa: punti di forza, sofferenze tattiche, consigli. Ogni manager ha 3 chiamate gratuite per stagione (illimitate per admin).",
    tone: "violet",
  },
  {
    icon: History,
    title: "📚 Storico permanente 3 stagioni",
    body: "Vai su Storico per vedere il palmares, i ranking, l'andamento della lega negli anni. Ogni giocatore mantiene la sua carriera completa dentro la lega — proprio come nel calcio reale.",
    tone: "rose",
  },
  {
    icon: Rocket,
    title: "🚀 Pronto a giocare davvero?",
    body: "Se ti piace, crea la tua lega vera per 69€ (39€ se è la tua prima e siamo tra ottobre e marzo). Un unico pagamento, tutta la stagione, tutti i tuoi manager reali.",
    tone: "emerald",
    cta: { label: "Crea la mia lega", href: "/register" },
  },
];

const TONE_STYLES = {
  amber: { icon: "text-amber-400", bg: "from-amber-500/[0.08]", border: "border-amber-500/40" },
  sky: { icon: "text-sky-400", bg: "from-sky-500/[0.08]", border: "border-sky-500/40" },
  emerald: { icon: "text-emerald-400", bg: "from-emerald-500/[0.08]", border: "border-emerald-500/40" },
  violet: { icon: "text-violet-400", bg: "from-violet-500/[0.08]", border: "border-violet-500/40" },
  rose: { icon: "text-rose-400", bg: "from-rose-500/[0.08]", border: "border-rose-500/40" },
};

export default function DemoTour({ enabled }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    if (localStorage.getItem("demo_tour_seen")) return;
    const t = setTimeout(() => setOpen(true), 600);
    return () => clearTimeout(t);
  }, [enabled]);

  function close() {
    localStorage.setItem("demo_tour_seen", "1");
    setOpen(false);
  }

  if (!enabled || !open) return null;

  const s = STEPS[step];
  const tone = TONE_STYLES[s.tone];
  const Icon = s.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-sm animate-in fade-in"
      onClick={close}
      data-testid="demo-tour-modal"
    >
      <div
        className={`relative w-full max-w-lg bg-slate-900 border ${tone.border} rounded-3xl shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          data-testid="demo-tour-close"
          className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors z-10"
          aria-label="Chiudi"
        >
          <X size={20} />
        </button>
        <div className={`bg-gradient-to-br ${tone.bg} via-transparent to-transparent p-8 pb-4`}>
          <Icon size={42} className={`${tone.icon} mb-4`} strokeWidth={1.75}/>
          <h2 className="text-2xl font-black tracking-tight leading-tight" data-testid="demo-tour-title">
            {s.title}
          </h2>
          <p className="mt-3 text-sm text-slate-300 leading-relaxed">
            {s.body}
          </p>
        </div>

        <div className="px-8 pb-6 pt-2 flex items-center justify-between">
          {/* Progress dots */}
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                aria-label={`Vai al passo ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? `w-6 ${tone.icon.replace("text", "bg")}` : "w-1.5 bg-slate-700 hover:bg-slate-600"
                }`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep((n) => n - 1)}
                data-testid="demo-tour-prev"
                className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-700 hover:border-slate-500 text-slate-300 text-sm transition-colors"
              >
                <ChevronLeft size={14}/> Indietro
              </button>
            )}
            {isLast && s.cta ? (
              <Link
                to={s.cta.href}
                onClick={close}
                data-testid="demo-tour-cta"
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-sm transition-colors`}
              >
                {s.cta.label} <ChevronRight size={14}/>
              </Link>
            ) : (
              <button
                onClick={() => (isLast ? close() : setStep((n) => n + 1))}
                data-testid="demo-tour-next"
                className={`inline-flex items-center gap-1 px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-sm transition-colors`}
              >
                {isLast ? "Iniziamo!" : "Avanti"} <ChevronRight size={14}/>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
